// apiService.js - Updated with BPMS Integration and New Verification Endpoint

import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
    //baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001/api',
    baseURL: process.env.REACT_APP_API_URL || 'https://eproficiency.oouagoiwoye.edu.ng/api',

    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Create BPMS API instance
const bpmsApi = axios.create({
    baseURL: process.env.REACT_APP_BPMS_API_URL || 'https://payments.oouagoiwoye.edu.ng/api',
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Token expired or invalid
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/';
        }
        return Promise.reject(error);
    }
);

// API service functions
const apiService = {
    // Authentication endpoints
    auth: {
        register: (userData) => api.post('/auth/register', userData),
        login: (credentials) => api.post('/auth/login', credentials),
        logout: () => api.post('/auth/logout'),
        forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
        validateResetToken: (token) => api.get(`/auth/reset-password/${token}`),
        resetPassword: (token, password) => api.post(`/auth/reset-password/${token}`, { password }),
        verifyEmail: (token) => api.get(`/auth/verify-email/${token}`),
        resendVerification: (email) => api.post('/auth/resend-verification', { email }),
        getProfile: () => api.get('/auth/profile'),
        updateProfile: (profileData) => api.put('/auth/profile', profileData),
        changePassword: (passwordData) => api.post('/auth/change-password', passwordData),
    },

    // Enhanced Verification endpoints with BPMS integration
    verification: {
        checkCertificate: (certificateNumber) =>
            api.post('/verification/check-certificate', { certificate_number: certificateNumber }),

        processPayment: (certificateNumber) =>
            api.post('/verification/process-payment', { certificate_number: certificateNumber }),

        // UPDATED: Use the new verification status endpoint that syncs with BPMS
        checkVerificationStatus: (transactionId) => {
            const currentUser = authHelpers.getCurrentUser();

            if (currentUser) {
                // User is logged in, use the authenticated endpoint
                return api.get(`/verification/check-verification-status/${transactionId}`);
            } else {
                // No user logged in, use the public endpoint
                return api.get(`/verification/public/payment-status/${transactionId}`);
            }
        },

        // Public endpoint specifically for non-authenticated access
        checkPublicPaymentStatus: (transactionId) =>
            api.get(`/verification/public/payment-status/${transactionId}`),

        // DEPRECATED: Use checkVerificationStatus instead
        checkPaymentStatus: (transactionId) =>
            api.get(`/verification/payment-status/${transactionId}`),

        getHistory: (page = 1, perPage = 20) =>
            api.get(`/verification/history?page=${page}&per_page=${perPage}`),

        getStatistics: () => api.get('/verification/statistics'),

        getCertificateDetails: (certificateNumber) =>
            api.get(`/verification/certificate/${certificateNumber}`),

        verifyBulk: (certificateNumbers) =>
            api.post('/verification/verify-bulk', { certificate_numbers: certificateNumbers }),

        getSettings: () => api.get('/verification/settings'),

        // Process bulk payment for multiple certificates
        processBulkPayment: (certificateNumbers) =>
            api.post('/verification/process-bulk-payment', { certificate_numbers: certificateNumbers }),

        // NEW: Manual payment reconciliation endpoints
        reconcileSinglePayment: (transactionId) =>
            api.post(`/verification/bpms/check/${transactionId}`),

        reconcileAllPending: () =>
            api.post('/verification/bpms/check-all-pending'),

        // NEW: Search transactions
        searchTransactions: (params = {}) => {
            const queryString = new URLSearchParams(params).toString();
            return api.get(`/verification/transactions/search?${queryString}`);
        },

        // NEW: Get transaction logs
        getTransactionLogs: (transactionId) =>
            api.get(`/verification/transactions/${transactionId}/logs`),
    },

    // BPMS Integration endpoints
    bpms: {
        // Initialize payment for general university services
        initializePayment: (paymentData) => bpmsApi.post('/payment', paymentData),

        // Initialize certificate verification payment specifically
        initializeCertificatePayment: (paymentData) =>
            bpmsApi.post('/certificate-payment', paymentData),

        // Get payment status
        getPaymentStatus: (transactionId) =>
            bpmsApi.get(`/payment/${transactionId}`),

        // Get certificate payment status
        getCertificatePaymentStatus: (transactionId) =>
            bpmsApi.get(`/certificate-payment/${transactionId}`),

        // Verify payment
        verifyPayment: (transactionId) =>
            bpmsApi.post(`/verify/${transactionId}`),

        // Get transaction details
        getTransaction: (transactionId) =>
            bpmsApi.get(`/transaction/${transactionId}`),

        // Get all transactions with filters
        getTransactions: (filters = {}) => {
            const queryString = new URLSearchParams(filters).toString();
            return bpmsApi.get(`/transaction?${queryString}`);
        },

        // Generate invoice
        generateInvoice: (invoiceData) => bpmsApi.post('/invoice', invoiceData),

        // Get invoice details
        getInvoice: (invoiceId) => bpmsApi.get(`/invoice/${invoiceId}`),

        // Health check
        healthCheck: () => bpmsApi.get('/health'),
    },

    // Admin endpoints
    admin: {
        // User management
        getUsers: (params = {}) => {
            const queryString = new URLSearchParams(params).toString();
            return api.get(`/admin/users?${queryString}`);
        },
        toggleUserStatus: (userId) => api.put(`/admin/users/${userId}/toggle-status`),
        verifyUserEmail: (userId) => api.put(`/admin/users/${userId}/verify-email`),
        updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
        // Edit user details
        updateUser: (userId, userData) =>
            api.put(`/admin/users/${userId}`, userData),
        // Create a new user
        createUser: (userData) =>
            api.post('/admin/users', userData),
        // Resend verification link
        resendVerificationEmail: (userId) =>
            api.post(`/admin/users/${userId}/resend-verification`),
        // Certificate management

        // Certificate management
        getCertificates: (params = {}) => {
            const qs = new URLSearchParams(params).toString();
            return api.get(`/admin/certificates?${qs}`);
        },
        createCertificate: (data) =>
            api.post('/admin/certificates', data),
        updateCertificate: (id, data) =>
            api.put(`/admin/certificates/${id}`, data),
        deleteCertificate: (id) =>
            api.delete(`/admin/certificates/${id}`),

        downloadCertificateTemplate: () =>
            api.get('/admin/certificates/template', { responseType: 'blob' }),
        uploadCertificates: (file) => {
            const formData = new FormData();
            formData.append('file', file);
            return api.post('/admin/certificates/upload', formData, {
                headers: {
                    // Remove Content-Type to let browser set it with boundary
                    'Content-Type': undefined
                },
                // OR use the transform method as in your current code
                transformRequest: [(data, headers) => {
                    delete headers['Content-Type'];
                    return data;
                }],
            });
        },
        // Authentication reports
        getAuthentications: (params = {}) => {
            const queryString = new URLSearchParams(params).toString();
            return api.get(`/admin/authentications?${queryString}`);
        },

        // System settings
        updateVerificationFee: (fee, currency = 'NGN') =>
            api.put('/admin/settings/verification-fee', { fee, currency }),
        getVerificationFee: () =>
            api.get('/admin/settings/verification-fee'),
        // Reports and analytics
        getUploadReports: (params = {}) => {
            const queryString = new URLSearchParams(params).toString();
            return api.get(`/admin/reports/upload-history?${queryString}`);
        },
        getDashboardAnalytics: () => api.get('/admin/analytics/dashboard'),

        // Activity logs
        getActivityLogs: (params = {}) => {
            const queryString = new URLSearchParams(params).toString();
            return api.get(`/admin/activity-logs?${queryString}`);
        },

        // BPMS Integration for Admin
        getBPMSTransactions: (params = {}) => {
            const queryString = new URLSearchParams(params).toString();
            return api.get(`/admin/bpms/transactions?${queryString}`);
        },

        getBPMSReports: (params = {}) => {
            const queryString = new URLSearchParams(params).toString();
            return api.get(`/admin/bpms/reports?${queryString}`);
        },

        reconcilePayments: (date) =>
            api.post('/admin/bpms/reconcile', { date }),

        // NEW: Admin bulk check endpoint
        bulkCheckPayments: () =>
            api.post('/verification/admin/bulk-check'),
    },

    // Utility endpoints
    health: () => api.get('/health'),

    // Payment utilities
    payment: {
        // Submit payment form programmatically (new tab)
        submitPaymentForm: (paymentData, targetUrl) => {
            return new Promise((resolve, reject) => {
                try {
                    const form = document.createElement('form');
                    form.method = 'POST';
                    form.action = targetUrl;
                    form.target = '_blank';
                    form.style.display = 'none';

                    // Add all payment data as hidden inputs
                    Object.keys(paymentData).forEach(key => {
                        const input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = key;
                        input.value = paymentData[key];
                        form.appendChild(input);
                    });

                    document.body.appendChild(form);
                    form.submit();
                    document.body.removeChild(form);

                    resolve({ success: true });
                } catch (error) {
                    reject(error);
                }
            });
        },

        // Open payment in popup window
        openPaymentPopup: (paymentData, targetUrl) => {
            return new Promise((resolve, reject) => {
                try {
                    const form = document.createElement('form');
                    form.method = 'POST';
                    form.action = targetUrl;
                    form.target = 'payment_popup';
                    form.style.display = 'none';

                    // Add all payment data as hidden inputs
                    Object.keys(paymentData).forEach(key => {
                        const input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = key;
                        input.value = paymentData[key];
                        form.appendChild(input);
                    });

                    // Open popup window
                    const popup = window.open(
                        '',
                        'payment_popup',
                        'width=800,height=600,scrollbars=yes,resizable=yes'
                    );

                    if (!popup) {
                        reject(new Error('Popup blocked. Please allow popups and try again.'));
                        return;
                    }

                    document.body.appendChild(form);
                    form.submit();
                    document.body.removeChild(form);

                    resolve({ success: true, popup });
                } catch (error) {
                    reject(error);
                }
            });
        },

        // Monitor payment popup
        monitorPaymentPopup: (popup, onClose, onSuccess) => {
            const checkClosed = setInterval(() => {
                if (popup.closed) {
                    clearInterval(checkClosed);
                    if (onClose) onClose();
                }
            }, 1000);

            // Listen for messages from popup
            const messageListener = (event) => {
                if (event.origin !== window.location.origin) return;

                if (event.data.type === 'PAYMENT_SUCCESS') {
                    clearInterval(checkClosed);
                    window.removeEventListener('message', messageListener);
                    popup.close();
                    if (onSuccess) onSuccess(event.data);
                }
            };

            window.addEventListener('message', messageListener);

            return () => {
                clearInterval(checkClosed);
                window.removeEventListener('message', messageListener);
            };
        },

        // NEW: Enhanced payment status checking with retries
        checkPaymentStatusWithRetry: async (transactionId, maxRetries = 3) => {
            let lastError;

            for (let attempt = 1; attempt <= maxRetries; attempt++) {
                try {
                    const response = await apiService.verification.checkVerificationStatus(transactionId);
                    return response;
                } catch (error) {
                    lastError = error;
                    console.warn(`Payment status check attempt ${attempt} failed:`, error.message);

                    if (attempt < maxRetries) {
                        // Wait before retrying (exponential backoff)
                        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
                        await new Promise(resolve => setTimeout(resolve, delay));
                    }
                }
            }

            throw lastError;
        },
    },
};

// Helper functions for common operations
export const authHelpers = {
    isAuthenticated: () => {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');
        return !!(token && user);
    },

    getCurrentUser: () => {
        const user = localStorage.getItem('user');
        return user ? JSON.parse(user) : null;
    },

    isAdmin: () => {
        const user = authHelpers.getCurrentUser();
        return user && ['admin', 'super_admin'].includes(user.role);
    },

    isSuperAdmin: () => {
        const user = authHelpers.getCurrentUser();
        return user && user.role === 'super_admin';
    },

    logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
    },

    login: (token, userData) => {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
    },
};

// Enhanced error handling helpers
export const errorHelpers = {
    getErrorMessage: (error) => {
        if (error.response?.data?.error) {
            return error.response.data.error;
        }
        if (error.response?.data?.message) {
            return error.response.data.message;
        }
        if (error.message) {
            return error.message;
        }
        return 'An unexpected error occurred';
    },

    getErrorCode: (error) => {
        return error.response?.data?.code || 'UNKNOWN_ERROR';
    },

    isNetworkError: (error) => {
        return !error.response && Boolean(error.request);
    },

    isServerError: (error) => {
        return error.response?.status >= 500;
    },

    isClientError: (error) => {
        return error.response?.status >= 400 && error.response?.status < 500;
    },

    isPaymentError: (error) => {
        const paymentErrorCodes = [
            'PAYMENT_FAILED',
            'PAYMENT_CANCELLED',
            'PAYMENT_TIMEOUT',
            'INSUFFICIENT_FUNDS',
            'INVALID_CARD',
            'CARD_DECLINED',
            'REQUERY_FAILED',
            'STATUS_CHECK_FAILED'
        ];
        return paymentErrorCodes.includes(errorHelpers.getErrorCode(error));
    },

    getPaymentErrorMessage: (error) => {
        const code = errorHelpers.getErrorCode(error);
        const paymentErrorMessages = {
            'PAYMENT_FAILED': 'Payment failed. Please try again or use a different payment method.',
            'PAYMENT_CANCELLED': 'Payment was cancelled. You can try again when ready.',
            'PAYMENT_TIMEOUT': 'Payment timed out. Please try again.',
            'INSUFFICIENT_FUNDS': 'Insufficient funds. Please check your account balance.',
            'INVALID_CARD': 'Invalid card details. Please check and try again.',
            'CARD_DECLINED': 'Card was declined. Please try a different card or contact your bank.',
            'REQUERY_FAILED': 'Unable to verify payment status. Please try again or contact support.',
            'STATUS_CHECK_FAILED': 'Failed to check payment status. Please try again later.'
        };
        return paymentErrorMessages[code] || errorHelpers.getErrorMessage(error);
    },
};

// Enhanced format helpers
export const formatHelpers = {
    formatDate: (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    },

    formatDateTime: (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    },

    formatCurrency: (amount, currency = 'NGN') => {
        if (amount === null || amount === undefined) return 'N/A';
        const numAmount = parseFloat(amount);
        if (isNaN(numAmount)) return 'N/A';

        const formatters = {
            'NGN': new Intl.NumberFormat('en-NG', {
                style: 'currency',
                currency: 'NGN',
                minimumFractionDigits: 2,
            }),
            'USD': new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
            }),
            'EUR': new Intl.NumberFormat('en-EU', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
            }),
            'GBP': new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: 'GBP',
                minimumFractionDigits: 2,
            }),
        };

        const formatter = formatters[currency] || formatters['NGN'];
        return formatter.format(numAmount);
    },

    formatNumber: (number) => {
        if (number === null || number === undefined) return 'N/A';
        return new Intl.NumberFormat('en-US').format(number);
    },

    truncateText: (text, maxLength) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substr(0, maxLength) + '...';
    },

    formatTransactionId: (transactionId) => {
        if (!transactionId) return 'N/A';
        // Format as XXXX-XXXX-XXXX for better readability
        return transactionId.replace(/(.{4})/g, '$1-').slice(0, -1);
    },

    formatPaymentStatus: (status) => {
        const statusLabels = {
            'PENDING': 'Pending',
            'APPROVED': 'Approved',
            'PAID': 'Paid',
            'FAILED': 'Failed',
            'CANCELLED': 'Cancelled',
            'PROCESSING': 'Processing',
            'paid': 'Paid',
            'failed': 'Failed',
            'pending': 'Pending',
            'success': 'Success',
            'not_found': 'Not Found'
        };
        return statusLabels[status] || status;
    },

    formatPaymentStatusColor: (status) => {
        const statusColors = {
            'PENDING': 'orange',
            'APPROVED': 'green',
            'PAID': 'green',
            'FAILED': 'red',
            'CANCELLED': 'gray',
            'PROCESSING': 'blue',
            'paid': 'green',
            'failed': 'red',
            'pending': 'orange',
            'success': 'green',
            'not_found': 'red'
        };
        return statusColors[status] || 'gray';
    },
};

// Enhanced validation helpers
export const validationHelpers = {
    validateEmail: (email) => {
        if (!email) return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    },

    validatePassword: (password) => {
        if (!password) {
            return {
                isValid: false,
                errors: { required: 'Password is required' }
            };
        }

        const minLength = password.length >= 8;
        const hasUpper = /[A-Z]/.test(password);
        const hasLower = /[a-z]/.test(password);
        const hasNumber = /\d/.test(password);
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        return {
            isValid: minLength && hasUpper && hasLower && hasNumber,
            errors: {
                minLength: !minLength ? 'Password must be at least 8 characters long' : null,
                hasUpper: !hasUpper ? 'Password must contain at least one uppercase letter' : null,
                hasLower: !hasLower ? 'Password must contain at least one lowercase letter' : null,
                hasNumber: !hasNumber ? 'Password must contain at least one number' : null,
                hasSpecial: !hasSpecial ? 'Password should contain at least one special character' : null,
            },
        };
    },

    validatePhone: (phone) => {
        if (!phone) return false;
        // Nigerian phone number validation
        const nigerianPhoneRegex = /^(\+234|234|0)?[789][01]\d{8}$/;
        const internationalPhoneRegex = /^[\+]?[1-9][\d]{0,15}$/;

        const cleanPhone = phone.replace(/\s|-|\(|\)/g, '');
        return nigerianPhoneRegex.test(cleanPhone) || internationalPhoneRegex.test(cleanPhone);
    },

    validateCertificateNumber: (certNumber) => {
        if (!certNumber) return false;
        // OOU certificate number format validation
        // Examples: UG/20/1234, PG/19/567, CCED/DP/20/890
        const oouCertRegex = /^(UG|PG|CCED|HND)\/[A-Z0-9\/]*\d{3,}$/i;
        const generalCertRegex = /^[A-Z0-9\/\-]{5,}$/i;

        const trimmed = certNumber.trim().toUpperCase();
        return oouCertRegex.test(trimmed) || generalCertRegex.test(trimmed);
    },

    validateAmount: (amount) => {
        if (amount === null || amount === undefined) return false;
        const numAmount = parseFloat(amount);
        return !isNaN(numAmount) && numAmount > 0 && numAmount <= 10000000; // Max 10M
    },

    validateTransactionId: (transactionId) => {
        if (!transactionId) return false;
        // BPMS transaction ID format validation
        const bpmsRegex = /^(BP|CVF|INV)\d{10,}$/;
        return bpmsRegex.test(transactionId);
    },

    sanitizeInput: (input) => {
        if (typeof input !== 'string') return input;
        return input.trim().replace(/[<>]/g, '');
    },
};

// Payment helpers
export const paymentHelpers = {
    // Calculate fees and totals
    calculateTotal: (amount, fees = []) => {
        const baseAmount = parseFloat(amount) || 0;
        const totalFees = fees.reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0);
        return baseAmount + totalFees;
    },

    // Generate payment reference
    generatePaymentReference: (prefix = 'PAY') => {
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(2, 8).toUpperCase();
        return `${prefix}_${timestamp}_${random}`;
    },

    // Check if payment method is supported
    isPaymentMethodSupported: (method) => {
        const supportedMethods = [
            'card',
            'bank_transfer',
            'ussd',
            'qr_code',
            'mobile_money',
            'bank_branch'
        ];
        return supportedMethods.includes(method);
    },

    // Format payment method display name
    formatPaymentMethod: (method) => {
        const methodNames = {
            'card': 'Debit/Credit Card',
            'bank_transfer': 'Bank Transfer',
            'ussd': 'USSD Code',
            'qr_code': 'QR Code',
            'mobile_money': 'Mobile Money',
            'bank_branch': 'Bank Branch'
        };
        return methodNames[method] || method;
    },

    // Check payment status polling interval
    getPollingInterval: (attempt) => {
        // Progressive interval: 5s, 10s, 15s, 30s, 60s
        const intervals = [5000, 10000, 15000, 30000, 60000];
        return intervals[Math.min(attempt, intervals.length - 1)];
    },

    // Check if payment should timeout
    shouldTimeout: (startTime, timeoutMinutes = 30) => {
        const elapsed = Date.now() - startTime;
        return elapsed > (timeoutMinutes * 60 * 1000);
    },

    // NEW: Enhanced status checking
    isPaymentComplete: (status) => {
        return ['paid', 'PAID', 'APPROVED', 'success'].includes(status);
    },

    isPaymentFailed: (status) => {
        return ['failed', 'FAILED', 'CANCELLED', 'DECLINED'].includes(status);
    },

    isPaymentPending: (status) => {
        return ['pending', 'PENDING', 'PROCESSING'].includes(status);
    },
};

// Storage helpers for payment data
export const storageHelpers = {
    // Save payment session
    savePaymentSession: (sessionData) => {
        const key = `payment_session_${sessionData.transactionId}`;
        localStorage.setItem(key, JSON.stringify({
            ...sessionData,
            timestamp: Date.now()
        }));
    },

    // Get payment session
    getPaymentSession: (transactionId) => {
        const key = `payment_session_${transactionId}`;
        const data = localStorage.getItem(key);
        return data ? JSON.parse(data) : null;
    },

    // Clear payment session
    clearPaymentSession: (transactionId) => {
        const key = `payment_session_${transactionId}`;
        localStorage.removeItem(key);
    },

    // Clean old payment sessions (older than 24 hours)
    cleanOldSessions: () => {
        const now = Date.now();
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('payment_session_')) {
                try {
                    const data = JSON.parse(localStorage.getItem(key));
                    if (data.timestamp && (now - data.timestamp) > maxAge) {
                        localStorage.removeItem(key);
                    }
                } catch (error) {
                    // Remove corrupted data
                    localStorage.removeItem(key);
                }
            }
        }
    },
};

export default apiService;